import React, { useContext, useEffect } from 'react';

import { FunFacts } from '../../components/FunFacts';
import { GetDataBox } from '../../components/GetData';
import { KaiScore } from '../../components/KaiScore';
import { Loader } from '../../components/Loader';
import { VisibleOn } from '../../components/VisibleOn';
import { VisibleOnVersion } from '../../components/VisibleOnVersion';
import { useStoryblokStrings } from '../../hooks';
import { useAPI } from '../../hooks/useAPI';
import { CoreContext } from '../../providers';
import { setError } from '../../state';
import { Storyblok } from '../../storyblok';
import { Api } from './api';
import { useLoanApplicationsQuery } from './api/onescore-loan-applications.query';
import { useCheckProgress } from './hooks/useCheckProgress';

interface ProcessProps {
    scoreTellerTitle: string;
    funFactsTitle: string;
    redirectPathOnFailure: string;
    backgroundImage: Storyblok.Field.Asset;
    setProcessStep: React.Dispatch<
        React.SetStateAction<'process' | 'confirmation'>
    >;
}

export const Process: React.FC<ProcessProps> = ({
    funFactsTitle,
    redirectPathOnFailure,
    scoreTellerTitle,
    setProcessStep,
}) => {
    const generalStrings = useStoryblokStrings('general-strings');

    const extract = Api.useExtract();

    const upload = Api.useUpload();

    const checkProgress = useCheckProgress();

    const loanApplications = useLoanApplicationsQuery({
        navigateRouteOnError: redirectPathOnFailure,
    });

    const { store, settings } = useContext(CoreContext);

    const { Altinn, Upscore } = useAPI();

    useEffect(() => {
        if (loanApplications.data) {
            try {
                const documents =
                    loanApplications.data.tax_documents.personal.tax_reports ??
                    [];

                uploadDocuments(documents);
            } catch (error) {
                console.error(error);
                store.dispatch(setError('general'));
            }
        }
    }, [JSON.stringify(loanApplications.data)]);

    useEffect(() => {
        if (loanApplications.isError) {
            store.dispatch(setError('general'));
        }
    }, [loanApplications.isError]);

    const uploadDocuments = async (documents: Array<any>) => {
        /**
         * Sequentially upload documents (one by one)
         */
        for (const document of documents) {
            const { filename, content } = document;
            const file = await fetch(content).then((res) => res.blob());
            const year = Altinn.extractYearFromAttachment(document);

            const dataToUpload = {
                file,
                fileName: filename,
                status: 'OK',
                target: 'my',
                year: year,
                dataType: 'taxReturn',
                fileType: 'pdf',
            } as any;

            await upload.mutateAsync(dataToUpload);
        }

        await extract.mutateAsync();

        checkProgress.startCheck();
    };

    return (
        <>
            <VisibleOnVersion versions={['upscore', 'pengestyrke']}>
                {scoreTellerTitle && (
                    <VisibleOn mobile>
                        <h2>{scoreTellerTitle}</h2>
                    </VisibleOn>
                )}
                <KaiScore
                    countUpInitialValue={1}
                    countUpMaximumValue={1000}
                    countDuration={5}
                    countUpLoopMode={true}
                    variant={
                        settings.languageVersion === 'upscore'
                            ? 'bubble'
                            : 'speedometer'
                    }
                />
                {funFactsTitle && <FunFacts title={funFactsTitle} />}
            </VisibleOnVersion>

            <VisibleOnVersion versions={['formue']}>
                <GetDataBox style={{ flexDirection: 'column' }}>
                    <div style={{ marginBottom: '20px' }}>
                        {generalStrings.loading}
                    </div>
                    <Loader backgroundColor="#ffffff" />
                </GetDataBox>
            </VisibleOnVersion>
        </>
    );
};
